import { getLocale, translate, useLocale } from "../localization";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import JoinCodeDialog from "../components/JoinCodeDialog";
import { usePageMetadata } from "../context/PageMetadataContext";
import { useAuth } from "../context/AuthContext";
import { useAnalytics } from "../context/AnalyticsContext";
import { AppButton } from "../components/ui";
import ExperiencesAPI from "../services/ExperiencesAPI";
import "./Viewer.scss";

const VIEWER_BASE_URL = process.env.REACT_APP_VIEWER_BASE_URL || "https://viewer.10k.science";
const VIEWER_AUTH_MESSAGE = "10k:viewer-auth";
const VIEWER_AUTH_READY_MESSAGE = "10k:viewer-auth-ready";
const COGNITO_REFRESH_SKEW_MS = 2 * 60 * 1000;
const MAX_REFRESH_DELAY_MS = 2_147_000_000;
const getApiResponseData = (response) => response?.data?.data ?? response?.data ?? null;

const Viewer = () => {
  const locale = useLocale();
  const navigate = useNavigate();
  const { experienceId: experienceIdFromPath } = useParams();
  const [searchParams] = useSearchParams();
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [isJoinCodeDialogOpen, setIsJoinCodeDialogOpen] = useState(false);
  const [experienceName, setExperienceName] = useState("");
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const iframeRef = useRef(null);
  const { setTitle, setShowTitleInHeader } = usePageMetadata();
  const { jwt, refreshAuthSession, permissions } = useAuth();
  const { markAnalyticsDirty, scheduleAnalyticsRefresh } = useAnalytics();

  useEffect(() => {
    window.scrollTo(0, 0);
    setShowTitleInHeader(false);
    setTitle(translate("Viewer"));
  }, [setShowTitleInHeader, setTitle, locale]);

  useEffect(() => {
    markAnalyticsDirty();

    return () => {
      markAnalyticsDirty();
      scheduleAnalyticsRefresh({ delayMs: 4000 });
    };
  }, [markAnalyticsDirty, scheduleAnalyticsRefresh]);

  const experienceIdFromQuery = searchParams.get("experienceId");

  const experienceId = useMemo(
    () => experienceIdFromPath || experienceIdFromQuery || "",
    [experienceIdFromPath, experienceIdFromQuery]
  );

  const embedUrl = useMemo(() => {
    if (!experienceId) {
      return "";
    }

    // Capture the initial language; subsequent changes use the existing auth message.
    return `${VIEWER_BASE_URL}/experiences/embed/${encodeURIComponent(experienceId)}?language=${encodeURIComponent(getLocale())}`;
  }, [experienceId]);

  const viewerOrigin = useMemo(() => {
    try {
      return new URL(VIEWER_BASE_URL).origin;
    } catch {
      return "";
    }
  }, []);

  const postViewerAuth = useCallback(() => {
    const targetWindow = iframeRef.current?.contentWindow;
    if (!targetWindow || !viewerOrigin || !jwt || !experienceId) {
      return;
    }

    targetWindow.postMessage(
      {
        type: VIEWER_AUTH_MESSAGE,
        experienceId,
        token: jwt,
        language: locale,
      },
      viewerOrigin,
    );
  }, [experienceId, jwt, viewerOrigin, locale]);

  useEffect(() => {
    const handleViewerMessage = (event) => {
      if (
        event.origin !== viewerOrigin ||
        event.source !== iframeRef.current?.contentWindow ||
        event.data?.type !== VIEWER_AUTH_READY_MESSAGE ||
        event.data?.experienceId !== experienceId
      ) {
        return;
      }

      postViewerAuth();
    };

    window.addEventListener("message", handleViewerMessage);
    return () => window.removeEventListener("message", handleViewerMessage);
  }, [experienceId, postViewerAuth, viewerOrigin]);

  useEffect(() => {
    postViewerAuth();
  }, [postViewerAuth]);

  useEffect(() => {
    if (!jwt || typeof refreshAuthSession !== "function") {
      return undefined;
    }

    let expiresAt;
    try {
      expiresAt = Number(jwtDecode(jwt)?.exp) * 1000;
    } catch {
      return undefined;
    }

    if (!Number.isFinite(expiresAt)) {
      return undefined;
    }

    const refreshDelay = Math.min(
      MAX_REFRESH_DELAY_MS,
      Math.max(1_000, expiresAt - Date.now() - COGNITO_REFRESH_SKEW_MS),
    );
    const refreshTimer = setTimeout(() => {
      void refreshAuthSession();
    }, refreshDelay);

    return () => clearTimeout(refreshTimer);
  }, [jwt, refreshAuthSession]);

  useEffect(() => {
    setIframeLoaded(false);
  }, [embedUrl]);

  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
    postViewerAuth();
  }, [postViewerAuth]);

  const openExitDialog = useCallback(() => {
    setIsExitDialogOpen(true);
  }, []);

  const openJoinCodeDialog = useCallback(() => {
    setIsJoinCodeDialogOpen(true);
  }, []);

  const closeExitDialog = useCallback(() => {
    setIsExitDialogOpen(false);
  }, []);

  const closeJoinCodeDialog = useCallback(() => {
    setIsJoinCodeDialogOpen(false);
  }, []);

  const handleConfirmBackToHome = useCallback(() => {
    setIsExitDialogOpen(false);
    navigate("/");
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    if (!experienceId) {
      setExperienceName("");
      return undefined;
    }

    const loadExperienceName = async () => {
      try {
        const response = await ExperiencesAPI.get(`experiences/${experienceId}`, {
          requiresAuth: true,
        });
        const experience = getApiResponseData(response);

        if (!cancelled) {
          setExperienceName(
            experience?.name || experience?.title || experience?.metadata?.name || ""
          );
        }
      } catch (error) {
        console.error("Failed to load experience details for join codes", error);
        if (!cancelled) {
          setExperienceName("");
        }
      }
    };

    loadExperienceName();

    return () => {
      cancelled = true;
    };
  }, [experienceId]);

  return (
    <main className="viewerPage">
      <section className="viewerPage__content">
        <div className="viewerPage__overlayControls">
          <AppButton
            className="viewerPage__overlayButton viewerPage__backButton"
            kind="secondary"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={openExitDialog}
          >{translate("Back to Home")}</AppButton>
          {permissions?.manageJoinCodes === true && <AppButton
            className="viewerPage__overlayButton viewerPage__joinButton"
            kind="secondary"
            onClick={openJoinCodeDialog}
            disabled={!experienceId}
          >{translate("Join Code")}</AppButton>}
        </div>

        {!embedUrl && (
          <div className="viewerPage__status">{translate("Select an experience from Home to launch the embedded viewer.")}</div>
        )}

        {embedUrl && (
          <div className="viewerPage__embed">
            {!iframeLoaded && (
              <div className="viewerPage__loader">
                <div className="viewerPage__loaderSpinner" />
                <p className="viewerPage__loaderText">{translate("Loading experience\u2026")}</p>
              </div>
            )}
            <iframe
              ref={iframeRef}
              src={embedUrl}
              title={translate("10k Web Viewer")}
              loading="lazy"
              allow="camera; microphone; fullscreen"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              onLoad={handleIframeLoad}
              style={{ opacity: iframeLoaded ? 1 : 0, transition: "opacity 400ms ease" }}
            />
          </div>
        )}

        <Dialog
          open={isExitDialogOpen}
          onClose={closeExitDialog}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            className: "viewerExitDialog__paper",
          }}
        >
          <DialogTitle className="viewerExitDialog__title">{translate("Quit preview?")}</DialogTitle>
          <DialogContent className="viewerExitDialog__content">{translate("Quit previewing the experience and go back home?")}</DialogContent>
          <DialogActions className="viewerExitDialog__actions">
            <AppButton kind="ghost" onClick={closeExitDialog}>{translate("Keep Previewing")}</AppButton>
            <AppButton kind="primary" onClick={handleConfirmBackToHome}>{translate("Back to Home")}</AppButton>
          </DialogActions>
        </Dialog>
        <JoinCodeDialog
          open={isJoinCodeDialogOpen}
          onClose={closeJoinCodeDialog}
          experienceId={experienceId}
          experienceName={experienceName}
        />
      </section>
    </main>
  );
};

export default Viewer;
